import { create } from 'zustand';
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware';

// The CUG gate. Signed-out travellers see the public rate; signing in with a
// real Google account unlocks the member "SuiteScanner+" rate. This
// login-gated public-vs-member split is the closed-user-group mechanism ETG
// requires to release CUG supply — see docs/CUG.md.
//
// `isMember` tracks `token !== null` and is kept as its own field so existing
// screens can keep reading it the same way they always have.
type SessionUser = Api.Auth.Google.User;

type MemberState = {
    isMember: boolean;
    token: string | null;
    user: SessionUser | null;
    promptOpen: boolean;
    signInWithSession: (token: string, user: SessionUser) => void;
    signOut: () => void;
    openPrompt: () => void;
    closePrompt: () => void;
};

type WebStorage = {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
    removeItem(key: string): void;
};

// Web-only app: persist through the browser's localStorage when present.
const browserStorage: StateStorage = {
    getItem: key => (globalThis as { localStorage?: WebStorage }).localStorage?.getItem(key) ?? null,
    setItem: (key, value) => (globalThis as { localStorage?: WebStorage }).localStorage?.setItem(key, value),
    removeItem: key => (globalThis as { localStorage?: WebStorage }).localStorage?.removeItem(key)
};

const useMember = create<MemberState>()(
    persist(
        set => ({
            isMember: false,
            token: null,
            user: null,
            promptOpen: false,
            signInWithSession: (token, user) => set({ isMember: true, token, user, promptOpen: false }),
            signOut: () => set({ isMember: false, token: null, user: null }),
            openPrompt: () => set({ promptOpen: true }),
            closePrompt: () => set({ promptOpen: false })
        }),
        {
            name: 'suitescanner-member',
            storage: createJSONStorage(() => browserStorage),
            // The session token/user are durable; the prompt is transient UI.
            partialize: state => ({ isMember: state.isMember, token: state.token, user: state.user })
        }
    )
);

const useIsMember = () => useMember(state => state.isMember);

// Zustand's `persist` writes to localStorage but never reads it back on its
// own — a sign-in that completes in another same-origin tab/window (the
// Google OAuth popup, in particular, when it finishes before the opener's
// own promise resolves) updates localStorage but leaves this tab's in-memory
// state untouched until a manual reload. `storage` only fires in OTHER tabs
// (never the one that wrote it), which is exactly what's needed here.
if (typeof window !== 'undefined') {
    window.addEventListener('storage', event => {
        if (event.key === 'suitescanner-member') {
            void useMember.persist.rehydrate();
        }
    });
}

export { useIsMember, useMember };
export type { SessionUser };
